// import createGenerateClassName from '@mui/styles/createGenerateClassName';
// import jssPreset from '@mui/styles/jssPreset';
// import { create } from 'jss';
// import jssExtend from 'jss-plugin-extend';
// import rtl from 'jss-rtl';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StyledEngineProvider } from '@mui/material/styles';
import routes from 'app/configs/routesConfig';
import { useMemo } from 'react';
import { Provider } from 'react-redux';
import ErrorBoundary from '@fuse/utils/ErrorBoundary';
import { enUS } from 'date-fns/locale/en-US';
import { bg } from 'date-fns/locale/bg';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getAuthUrl } from '@fuse/utils/environement';
import AppContext from './AppContext';
import store from './store/store';

type ComponentProps = {
	name?: string;
};

// Create a client
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
			staleTime: 5 * 60 * 1000, // 5 minutes
		},
	},
});

/**
 * A Higher Order Component that provides the necessary context providers for the app.
 */
function withAppProviders(Component: React.ComponentType<ComponentProps>) {
	/**
	 * The component that wraps the provided component with the necessary context providers.
	 */
	function WithAppProviders(props: React.PropsWithChildren<ComponentProps>) {
		const { i18n } = useTranslation();

		/**
		 * The value to pass to the AppContext provider.
		 */
		const val = useMemo(
			() => ({
				routes
			}),
			[routes]
		);

		// Get the appropriate date-fns locale based on current language
		const dateLocale = useMemo(() => {
			try {
				return i18n.language === 'bg' ? bg : enUS;
			} catch (error) {
				console.warn('Date locale selection error, falling back to enUS:', error);
				return enUS;
			}
		}, [i18n.language]);

		return (
			<ErrorBoundary>
				<QueryClientProvider client={queryClient}>
					<AppContext.Provider value={val}>
						<LocalizationProvider
							dateAdapter={AdapterDateFns}
							adapterLocale={dateLocale}
						>
							<Provider store={store}>
								<StyledEngineProvider injectFirst>
									<Component {...props} />
								</StyledEngineProvider>
							</Provider>
						</LocalizationProvider>
					</AppContext.Provider>
				</QueryClientProvider>
			</ErrorBoundary>
		);
	}

	return WithAppProviders;
}

export default withAppProviders;
